import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, addDoc, updateDoc, doc, serverTimestamp, deleteDoc } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import { useAuth } from '../../contexts/AuthContext';
import { HiOutlineCalendarDays, HiOutlineClock, HiOutlineUserGroup, HiOutlineMapPin, HiOutlineUser, HiOutlinePhone, HiOutlineCurrencyEuro, HiOutlineWallet, HiOutlineXMark, HiOutlineChevronLeft, HiOutlineLink, HiOutlinePlusCircle, HiOutlineTrash, HiOutlineExclamationTriangle, HiOutlineMegaphone } from 'react-icons/hi2';
import { FaInstagram, FaTiktok, FaYoutube } from 'react-icons/fa';
import { MdOutlineExplore, MdOutlineOpenInNew } from 'react-icons/md';
import './MyBookings.css';

const MyBookings = () => {
    const { currentUser } = useAuth();
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('upcoming');
    const [selectedBooking, setSelectedBooking] = useState(null);

    // Posts state
    const [posts, setPosts] = useState([]);
    const [postsLoading, setPostsLoading] = useState(false);
    const [postSubmitting, setPostSubmitting] = useState(false);
    const [newPost, setNewPost] = useState({ platform: 'Instagram', link: '' });
    const [snackbar, setSnackbar] = useState({ open: false, message: '' });

    const PLATFORMS = [
        { value: 'Instagram', icon: <FaInstagram />, color: '#E4405F' },
        { value: 'TikTok', icon: <FaTiktok />, color: '#000000' },
        { value: 'YouTube', icon: <FaYoutube />, color: '#FF0000' },
        { value: 'Other', icon: <HiOutlineLink />, color: '#6b7280' },
    ];

    useEffect(() => {
        fetchBookings();
    }, [currentUser]);

    useEffect(() => {
        if (snackbar.open) {
            const timer = setTimeout(() => setSnackbar({ open: false, message: '' }), 3000);
            return () => clearTimeout(timer);
        }
    }, [snackbar.open]);

    const fetchBookings = async () => {
        if (!currentUser) return;

        try {
            setLoading(true);
            const bookingsRef = collection(db, 'experienceBookings');
            const q = query(bookingsRef, where('userId', '==', currentUser.uid));
            const querySnapshot = await getDocs(q);

            const bookingsData = [];
            for (const docSnapshot of querySnapshot.docs) {
                const booking = { id: docSnapshot.id, ...docSnapshot.data() };
                if (booking.paymentStatus === 'completed' && booking.bookingFromAffiliateWebsite) {
                    bookingsData.push(booking);
                }
            }

            // Sort by date descending
            bookingsData.sort((a, b) => {
                if (a.date > b.date) return -1;
                if (a.date < b.date) return 1;
                return 0;
            });

            setBookings(bookingsData);
        } catch (error) {
            console.error('Error fetching bookings:', error);
        } finally {
            setLoading(false);
        }
    };

    const getTodayStr = () => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    const getFilteredBookings = () => {
        const today = getTodayStr();
        switch (activeTab) {
            case 'upcoming':
                return bookings.filter(b => b.date >= today && b.status !== 'cancelled');
            case 'past':
                return bookings.filter(b => b.date < today && b.status !== 'cancelled');
            case 'cancelled':
                return bookings.filter(b => b.status === 'cancelled');
            default:
                return bookings;
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatDateLong = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getStatusClass = (status) => {
        switch (status) {
            case 'confirmed': return 'status-confirmed';
            case 'pending': return 'status-pending';
            case 'cancelled': return 'status-cancelled';
            default: return 'status-default';
        }
    };

    // Fetch posts when a booking is selected
    useEffect(() => {
        if (selectedBooking) {
            fetchPosts(selectedBooking.id);
        } else {
            setPosts([]);
        }
    }, [selectedBooking?.id]);

    const fetchPosts = async (bookingId) => {
        try {
            setPostsLoading(true);
            const postsRef = collection(db, 'affiliatePosts');
            const q = query(postsRef, where('bookingId', '==', bookingId));
            const querySnapshot = await getDocs(q);
            const postsData = querySnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            postsData.sort((a, b) => {
                const aTime = a.createdAt?.toMillis?.() || 0;
                const bTime = b.createdAt?.toMillis?.() || 0;
                return bTime - aTime;
            });
            setPosts(postsData);
        } catch (error) {
            console.error('Error fetching posts:', error);
        } finally {
            setPostsLoading(false);
        }
    };

    const handleAddPost = async () => {
        if (!newPost.link.trim() || !selectedBooking) return;

        try {
            setPostSubmitting(true);
            const postDoc = await addDoc(collection(db, 'affiliatePosts'), {
                bookingId: selectedBooking.id,
                experienceId: selectedBooking.experienceId,
                experienceName: selectedBooking.experienceName,
                affiliateId: currentUser.uid,
                affiliateEmail: currentUser.email,
                platform: newPost.platform,
                link: newPost.link.trim(),
                createdAt: serverTimestamp()
            });

            // Update hasPosted flag on booking if not already set
            if (!selectedBooking.hasPosted) {
                await updateDoc(doc(db, 'experienceBookings', selectedBooking.id), {
                    hasPosted: true,
                    updatedAt: new Date().toISOString()
                });
                // Update local booking state
                setSelectedBooking(prev => ({ ...prev, hasPosted: true }));
                setBookings(prev => prev.map(b =>
                    b.id === selectedBooking.id ? { ...b, hasPosted: true } : b
                ));
            }

            // Add to local posts list
            setPosts(prev => [{ id: postDoc.id, platform: newPost.platform, link: newPost.link.trim(), createdAt: { toMillis: () => Date.now() } }, ...prev]);
            setNewPost({ platform: 'Instagram', link: '' });
        } catch (error) {
            console.error('Error adding post:', error);
            alert('Failed to add post. Please try again.');
        } finally {
            setPostSubmitting(false);
        }
    };

    const handleDeletePost = async (postId) => {
        if (!window.confirm('Are you sure you want to remove this post?')) return;

        try {
            await deleteDoc(doc(db, 'affiliatePosts', postId));
            const updatedPosts = posts.filter(p => p.id !== postId);
            setPosts(updatedPosts);

            // If no posts remain, unset hasPosted
            if (updatedPosts.length === 0) {
                await updateDoc(doc(db, 'experienceBookings', selectedBooking.id), {
                    hasPosted: false,
                    updatedAt: new Date().toISOString()
                });
                setSelectedBooking(prev => ({ ...prev, hasPosted: false }));
                setBookings(prev => prev.map(b =>
                    b.id === selectedBooking.id ? { ...b, hasPosted: false } : b
                ));
            }
        } catch (error) {
            console.error('Error deleting post:', error);
            alert('Failed to remove post. Please try again.');
        }
    };

    const getPlatformInfo = (platform) => {
        return PLATFORMS.find(p => p.value === platform) || PLATFORMS[3];
    };

    const filteredBookings = getFilteredBookings();

    if (loading) {
        return (
            <div className="mb-page">
                <div className="mb-loading">
                    <div className="spinner"></div>
                    <p>Loading your bookings...</p>
                </div>
            </div>
        );
    }

    // Detail View
    if (selectedBooking) {
        return (
            <>
            <div className="mb-page">
                <div className="mb-wrapper">
                    <button className="mb-back-btn" onClick={() => setSelectedBooking(null)}>
                        <HiOutlineChevronLeft />
                        Back to Bookings
                    </button>

                    <div className="mb-detail-card">
                        <div className="mb-detail-header">
                            <MdOutlineExplore />
                            <h1>Booking Details</h1>
                            <span className={`mb-status-badge ${getStatusClass(selectedBooking.status)}`}>
                                {selectedBooking.status}
                            </span>
                        </div>

                        <div className="mb-detail-body">
                            {/* Experience Info */}
                            <div className="mb-detail-section">
                                <h3 className="mb-detail-section-title">
                                    <MdOutlineExplore />
                                    Experience
                                </h3>
                                <div className="mb-detail-experience-header">
                                    {selectedBooking.experienceImage && (
                                        <img
                                            src={selectedBooking.experienceImage}
                                            alt={selectedBooking.experienceName}
                                            className="mb-detail-thumb"
                                        />
                                    )}
                                    <div className="mb-detail-experience-info">
                                        <h4 className="mb-detail-experience-name">{selectedBooking.experienceName}</h4>
                                        {selectedBooking.optionName && (
                                            <p className="mb-detail-option">{selectedBooking.optionName} ({selectedBooking.optionType})</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Booking Details */}
                            <div className="mb-detail-section">
                                <h3 className="mb-detail-section-title">
                                    <HiOutlineCalendarDays />
                                    Schedule
                                </h3>
                                <div className="mb-detail-row">
                                    <span className="mb-detail-label">Date</span>
                                    <span className="mb-detail-value">{formatDateLong(selectedBooking.date)}</span>
                                </div>
                                <div className="mb-detail-row">
                                    <span className="mb-detail-label">Time</span>
                                    <span className="mb-detail-value">{selectedBooking.startTime} - {selectedBooking.endTime}</span>
                                </div>
                                <div className="mb-detail-row">
                                    <span className="mb-detail-label">Duration</span>
                                    <span className="mb-detail-value">{selectedBooking.duration}h{selectedBooking.durationMinutes ? ` ${selectedBooking.durationMinutes}m` : ''}</span>
                                </div>
                                <div className="mb-detail-row">
                                    <span className="mb-detail-label">Guests</span>
                                    <span className="mb-detail-value">{selectedBooking.guestCount} {selectedBooking.guestCount === 1 ? 'person' : 'people'}</span>
                                </div>
                            </div>

                            {/* Host Info */}
                            <div className="mb-detail-section">
                                <h3 className="mb-detail-section-title">
                                    <HiOutlineUser />
                                    Host Information
                                </h3>
                                <div className="mb-detail-row">
                                    <span className="mb-detail-label">Host Name</span>
                                    <span className="mb-detail-value">{selectedBooking.hostName}</span>
                                </div>
                                {selectedBooking.hostPhone && (
                                    <div className="mb-detail-row">
                                        <span className="mb-detail-label">Phone</span>
                                        <span className="mb-detail-value">+{selectedBooking.hostPhone}</span>
                                    </div>
                                )}
                            </div>

                            {/* Meeting Point */}
                            {selectedBooking.meetingPoint && (
                                <div className="mb-detail-section">
                                    <h3 className="mb-detail-section-title">
                                        <HiOutlineMapPin />
                                        Meeting Point
                                    </h3>
                                    <div className="mb-detail-row">
                                        <span className="mb-detail-label">Location</span>
                                        <span className="mb-detail-value">{selectedBooking.meetingPoint}</span>
                                    </div>
                                    {selectedBooking.meetingPointDescription && (
                                        <div className="mb-detail-row">
                                            <span className="mb-detail-label">Description</span>
                                            <span className="mb-detail-value">{selectedBooking.meetingPointDescription}</span>
                                        </div>
                                    )}
                                    {selectedBooking.googleMapsLink && (
                                        <div className="mb-detail-row">
                                            <span className="mb-detail-label">Directions</span>
                                            <span className="mb-detail-value">
                                                <a href={selectedBooking.googleMapsLink} target="_blank" rel="noopener noreferrer" className="mb-link">
                                                    View on Google Maps
                                                </a>
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Payment Summary */}
                            {selectedBooking.priceBreakdown && (
                                <div className="mb-detail-section">
                                    <h3 className="mb-detail-section-title">
                                        <HiOutlineCurrencyEuro />
                                        Payment Summary
                                    </h3>
                                    <div className="mb-detail-row">
                                        <span className="mb-detail-label">Base Price</span>
                                        <span className="mb-detail-value">€{selectedBooking.priceBreakdown.basePrice?.toFixed(2)}</span>
                                    </div>
                                    {selectedBooking.priceBreakdown.discount > 0 && (
                                        <div className="mb-detail-row">
                                            <span className="mb-detail-label">Affiliate Discount</span>
                                            <span className="mb-detail-value mb-savings">{selectedBooking.priceBreakdown.discount}% off</span>
                                        </div>
                                    )}
                                    {selectedBooking.priceBreakdown.balanceUsed > 0 && (
                                        <div className="mb-detail-row">
                                            <span className="mb-detail-label">Balance Used</span>
                                            <span className="mb-detail-value mb-savings">-€{selectedBooking.priceBreakdown.balanceUsed?.toFixed(2)}</span>
                                        </div>
                                    )}
                                    <div className="mb-detail-row mb-total-row">
                                        <span className="mb-detail-label">Total Paid</span>
                                        <span className="mb-detail-value">
                                            {selectedBooking.priceBreakdown.amountCharged === 0
                                                ? 'Paid with Balance'
                                                : `€${(selectedBooking.priceBreakdown.amountCharged ?? selectedBooking.priceBreakdown.finalPrice)?.toFixed(2)}`}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* Order ID */}
                            <div className="mb-detail-section">
                                <div className="mb-detail-row">
                                    <span className="mb-detail-label">Booking ID</span>
                                    <span className="mb-detail-value mb-order-id">{selectedBooking.id}</span>
                                </div>
                                <div className="mb-detail-row">
                                    <span className="mb-detail-label">Order ID</span>
                                    <span className="mb-detail-value mb-order-id">{selectedBooking.orderId}</span>
                                </div>
                            </div>

                            {/* Experience Detail */}
                            <div className="mb-detail-section">
                                <div className="mb-detail-row">
                                    <span className="mb-detail-label">Experience Link</span>
                                    <span className="mb-detail-value mb-order-id mb-copyable-link" onClick={() => {
                                        try {
                                            navigator.clipboard.writeText(`https://www.bosphorusyachts.com/experience/${selectedBooking.experienceId}`);
                                            setSnackbar({ open: true, message: 'Link copied to clipboard!' });
                                        } catch (err) {
                                            setSnackbar({ open: true, message: 'Could not copy link automatically' });
                                        }
                                    }}>
                                        https://www.bosphorusyachts.com/experience/{selectedBooking.experienceId}
                                        <HiOutlineLink style={{ marginLeft: '6px', cursor: 'pointer', flexShrink: 0 }} />
                                    </span>
                                </div>
                                <p className="mb-link-note">Add this link to your posts along with your affiliate discount code so your audience can book directly.</p>
                            </div>

                            {/* Posts Section */}
                            <div className="mb-detail-section mb-posts-section">
                                <h3 className="mb-detail-section-title">
                                    <HiOutlineMegaphone />
                                    Social Media Posts
                                </h3>

                                {!selectedBooking.hasPosted && (
                                    <div className="mb-post-warning">
                                        <HiOutlineExclamationTriangle />
                                        <div>
                                            <strong>Post Required</strong>
                                            <p>Please share this experience on your social media and add the post link below.</p>
                                        </div>
                                    </div>
                                )}

                                {/* Add Post Form */}
                                <div className="mb-post-form">
                                    <div className="mb-post-form-row">
                                        <div className="mb-platform-selector">
                                            {PLATFORMS.map(p => (
                                                <button
                                                    key={p.value}
                                                    className={`mb-platform-btn ${newPost.platform === p.value ? 'active' : ''}`}
                                                    onClick={() => setNewPost(prev => ({ ...prev, platform: p.value }))}
                                                    title={p.value}
                                                    style={newPost.platform === p.value ? { borderColor: p.color, background: `${p.color}18` } : {}}
                                                >
                                                    {p.icon}
                                                    <span>{p.value}</span>
                                                </button>
                                            ))}
                                        </div>
                                        <div className="mb-post-input-row">
                                            <input
                                                type="url"
                                                className="mb-post-input"
                                                placeholder="Paste your post link here..."
                                                value={newPost.link}
                                                onChange={(e) => setNewPost(prev => ({ ...prev, link: e.target.value }))}
                                                onKeyDown={(e) => e.key === 'Enter' && handleAddPost()}
                                            />
                                            <button
                                                className="mb-post-add-btn"
                                                onClick={handleAddPost}
                                                disabled={!newPost.link.trim() || postSubmitting}
                                            >
                                                <HiOutlinePlusCircle />
                                                {postSubmitting ? 'Adding...' : 'Add Post'}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Posts List */}
                                {postsLoading ? (
                                    <div className="mb-posts-loading">
                                        <p>Loading posts...</p>
                                    </div>
                                ) : posts.length > 0 ? (
                                    <div className="mb-posts-list">
                                        {posts.map(post => {
                                            const platformInfo = getPlatformInfo(post.platform);
                                            return (
                                                <div key={post.id} className="mb-post-item">
                                                    <div className="mb-post-platform" style={{ color: platformInfo.color }}>
                                                        {platformInfo.icon}
                                                    </div>
                                                    <div className="mb-post-info">
                                                        <span className="mb-post-platform-name">{post.platform}</span>
                                                        <a href={post.link} target="_blank" rel="noopener noreferrer" className="mb-post-link">
                                                            {post.link}
                                                            <MdOutlineOpenInNew />
                                                        </a>
                                                    </div>
                                                    <button
                                                        className="mb-post-delete-btn"
                                                        onClick={() => handleDeletePost(post.id)}
                                                        title="Remove post"
                                                    >
                                                        <HiOutlineTrash />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <p className="mb-posts-empty">No posts added yet. Share your experience and add the link above!</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {snackbar.open && (
                <div className="mb-snackbar">
                    <span>{snackbar.message}</span>
                    <button onClick={() => setSnackbar({ open: false, message: '' })}>×</button>
                </div>
            )}
            </>
        );
    }

    // List View
    return (
        <>
        <div className="mb-page">
            <div className="mb-wrapper">
                <div className="mb-page-header">
                    <h1 className="mb-page-title">My Bookings</h1>
                    <p className="mb-page-subtitle">{bookings.length} total booking{bookings.length !== 1 ? 's' : ''}</p>
                </div>

                {/* Tabs */}
                <div className="mb-tabs">
                    {['upcoming', 'past', 'cancelled'].map(tab => (
                        <button
                            key={tab}
                            className={`mb-tab ${activeTab === tab ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab)}
                        >
                            {tab.charAt(0).toUpperCase() + tab.slice(1)}
                            <span className="mb-tab-count">
                                {tab === 'upcoming'
                                    ? bookings.filter(b => b.date >= getTodayStr() && b.status !== 'cancelled').length
                                    : tab === 'past'
                                    ? bookings.filter(b => b.date < getTodayStr() && b.status !== 'cancelled').length
                                    : bookings.filter(b => b.status === 'cancelled').length
                                }
                            </span>
                        </button>
                    ))}
                </div>

                {/* Bookings List */}
                {filteredBookings.length === 0 ? (
                    <div className="mb-empty">
                        <MdOutlineExplore className="mb-empty-icon" />
                        <h3>No {activeTab} bookings</h3>
                        <p>Your {activeTab} experience bookings will appear here.</p>
                    </div>
                ) : (
                    <div className="mb-bookings-list">
                        {filteredBookings.map(booking => (
                            <div
                                key={booking.id}
                                className="mb-booking-card"
                                onClick={() => setSelectedBooking(booking)}
                            >
                                <div className="mb-card-image-section">
                                    {booking.experienceImage ? (
                                        <img
                                            src={booking.experienceImage}
                                            alt={booking.experienceName}
                                            className="mb-card-thumb"
                                        />
                                    ) : (
                                        <div className="mb-card-thumb-placeholder">
                                            <MdOutlineExplore />
                                        </div>
                                    )}
                                </div>
                                <div className="mb-card-content">
                                    <div className="mb-card-top">
                                        <h3 className="mb-card-title">{booking.experienceName}</h3>
                                        <div className="mb-card-badges">
                                            {!booking.hasPosted && (
                                                <span className="mb-status-badge status-warning" title="Post required">
                                                    <HiOutlineExclamationTriangle /> Post
                                                </span>
                                            )}
                                            <span className={`mb-status-badge ${getStatusClass(booking.status)}`}>
                                                {booking.status}
                                            </span>
                                        </div>
                                    </div>
                                    {booking.optionName && (
                                        <p className="mb-card-option">{booking.optionName}</p>
                                    )}
                                    <div className="mb-card-details">
                                        <span className="mb-card-detail">
                                            <HiOutlineCalendarDays />
                                            {formatDate(booking.date)}
                                        </span>
                                        <span className="mb-card-detail">
                                            <HiOutlineClock />
                                            {booking.startTime} - {booking.endTime}
                                        </span>
                                        <span className="mb-card-detail">
                                            <HiOutlineUserGroup />
                                            {booking.guestCount} guest{booking.guestCount !== 1 ? 's' : ''}
                                        </span>
                                    </div>
                                    <div className="mb-card-footer">
                                        <span className="mb-card-price">
                                            €{(booking.priceBreakdown?.amountCharged ?? booking.priceBreakdown?.finalPrice)?.toFixed(2)}
                                            {booking.priceBreakdown?.balanceUsed > 0 && (
                                                <span className="mb-card-balance-tag">+ Balance</span>
                                            )}
                                        </span>
                                        <span className="mb-card-arrow">→</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
        {snackbar.open && (
            <div className="mb-snackbar">
                <span>{snackbar.message}</span>
                <button onClick={() => setSnackbar({ open: false, message: '' })}>×</button>
            </div>
        )}
        </>
    );
};

export default MyBookings;
